import { Stack, Typography } from '@mui/material';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useFragment } from 'react-relay';
import { graphql } from 'relay-runtime';

import { COMPANY_PROJECT_TYPES_ENUM } from '@woovi/roles';
import { CheckIcon, PixPaymentWoovi } from '@woovi/ui';
import { convertToMoney } from '@woovi/utils';

import type { PaymentPixAutomaticCompleted_subscription$key } from './__generated__/PaymentPixAutomaticCompleted_subscription.graphql';
import PaymentPixAutomaticAdditionalInfo from './PaymentPixAutomaticAdditionalInfo.tsx';
import PaymentPixAutomaticRedirect from './PaymentPixAutomaticRedirect.tsx';
import { PaymentPixAutomaticTransactionInfo } from './PaymentPixAutomaticTransactionInfo.tsx';
import PaymentCompanyLogo from '../logo/PaymentCompanyLogo.tsx';

type PaymentPixAutomaticCompletedProps = {
  pixAutomatic: PaymentPixAutomaticCompleted_subscription$key;
};

const PaymentPixAutomaticCompleted = (
  props: PaymentPixAutomaticCompletedProps,
): ReactNode => {
  const { t } = useTranslation();

  const pixAutomatic =
    useFragment<PaymentPixAutomaticCompleted_subscription$key>(
      graphql`
        fragment PaymentPixAutomaticCompleted_subscription on PaymentSubscription {
          value
          company {
            nameFriendly
            ...PaymentCompanyLogo_company
          }
          ...PaymentPixAutomaticAdditionalInfo_subscription
          ...PaymentPixAutomaticRedirect_subscription
          ...PaymentPixAutomaticTransactionInfo_subscription
        }
      `,
      props.pixAutomatic,
    );

  const title = t('Pix Automatic of {{value}} to {{merchant}} approved', {
    value: convertToMoney(pixAutomatic?.value),
    merchant: pixAutomatic.company?.nameFriendly,
  });

  return (
    <Stack sx={{ alignItems: 'center', gap: 2 }}>
      <PaymentCompanyLogo company={pixAutomatic.company} />
      <CheckIcon />
      <Typography variant='h5' fontWeight='bold' textAlign='center'>
        {title}
      </Typography>
      <PaymentPixAutomaticTransactionInfo pixAutomatic={pixAutomatic} />
      <PaymentPixAutomaticAdditionalInfo pixAutomatic={pixAutomatic} />
      <PaymentPixAutomaticRedirect pixAutomatic={pixAutomatic} />
      <PixPaymentWoovi projectType={COMPANY_PROJECT_TYPES_ENUM.WOOVI} isTemp />
    </Stack>
  );
};

export default PaymentPixAutomaticCompleted;
